import { withTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { request } from "../../../../../utils/Request";
import Tooltip from "../../../../../components/Common/Display/Tooltip";
import HasAccess from "../../../../../utils/HasAccess";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import StateLessGrid from "../../../../../components/Shared/StateLessGrid";
import QuickCopyDropdown from "../../../../../components/Shared/QuickCopyDropdown";
import { Button } from "primereact/button";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { InputField } from "../../../../../components/Shared/InputField";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { utcDateToLocalDate } from "../../../../../utils/Helper";
import { ButtonGroup } from "primereact/buttongroup";
import ProviderForm from "./ProviderForm";
import ProviderFilter from "./ProviderFilter";
import ProviderExcel from "./ProviderExcel";

const ProviderList = (props: any) => {
    const { t, moduleState, toastify } = props;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [operation, setOperation] = useState<any>(searchParams.get("operation") ?? null);
    const [operationId, setOperationId] = useState<any>(searchParams.get("operationId") ?? null);
    const [filterPanel, setFilterPanel] = useState<boolean>(false);
    const [excelPanel, setExcelPanel] = useState<boolean>(false);
    const [initSearchFormState, setInitSearchFormState] = useState<any>({
        search: moduleState.search ?? null,
    });
    const [total, setTotal] = useState(0);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const tableRef: any = useRef();

    useEffect(() => {
        setInitSearchFormState({
            search: moduleState.search ?? null,
        });
    }, [moduleState.search]);

    const onDelete = async (payloads: any) => {
        try {
            await request({
                url: `suppliers/settings/providers/${ payloads.id }`,
                method: "DELETE",
                data: { _method: "DELETE" },
            });
            toastify(t("record deleted", { item: t("provider") }), "info");
            onReset();
        } catch (e: any) {
            if (e.status === 422) {
                return toastify(e.data.message, "error");
            }
            toastify(t("server error"), "error");
        }
    };

    const columns = [
        { field: "title", title: t("title"), sorting: true },
        { field: "slug", title: t("slug"), sorting: true },
        {
            field: "created_at",
            title: t("created at"),
            sorting: true,
            render: (_row: any) => utcDateToLocalDate(_row.created_at),
        },
        {
            field: "action",
            title: t("action"),
            sorting: false,
            align: "left",
            render: (_row: any) => (
                <>
                    {(_row.slug !== "default" && _row.slug !== "emil_frey" && _row.slug !== "vehicle_logistics") && <>
                        <HasAccess hasAllAccess={ ["providersupdate"] }>
                            <Tooltip tooltip={ t("edit item") }>
                                <a onClick={ () => onOperationClick("edit", _row.id) }>
                                    <i className="fas fa-edit" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                        <HasAccess hasAllAccess={ ["providersdestroy"] }>
                            <Tooltip tooltip={ t("delete item") }>
                                <a
                                    className={ "cursor-pointer ms-2 btn btn-soft-danger waves-effect waves-light btn-xs" }
                                    onClick={ (event: any) =>
                                        confirmPopup({
                                            target: event.currentTarget,
                                            message: t("do you want to delete this record?"),
                                            icon: "pi pi-info-circle",
                                            // @ts-ignore
                                            defaultFocus: "reject",
                                            acceptClassName: "p-button-danger",
                                            accept: () => onDelete(_row),
                                            reject: () => {
                                            },
                                        })
                                    }
                                >
                                    <i className="fas fa-trash-alt" />
                                </a>
                            </Tooltip>
                        </HasAccess>
                    </>}
                </>
            ),
        },
    ];

    const onOperationClick = (operationToBePerformed: any = "add", id: any = null) => {
        const sp = new URLSearchParams(location.search);
        sp.set("operation", operationToBePerformed);
        sp.set("operationId", id);

        if (isEmpty(operationToBePerformed)) {
            sp.delete("operation");
        }
        if (isEmpty(id)) {
            sp.delete("operationId");
        }

        navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
        setOperationId(id);
        setOperation(operationToBePerformed);
    };

    const onCloseOperation = (reset: any = true) => {
        onOperationClick(null, null);
        onReset(reset);
        focusOnSearch();
    };

    const onReset = (reset: any = true) => {
        const mStates = { ...moduleState };
        if (reset) {
            mStates.filters = [];
            mStates.page = 1;
            mStates.search = null;
        }
        props.setQuery(mStates);
    };

    const onSearch = ({ search }: any) => {
        props.setQuery({
            ...moduleState,
            search,
        });
    };

    const focusOnSearch = () => {
        const el: HTMLInputElement | null = document.querySelector('input[name="search"]');
        if (el) {
            el.focus();
        }
    };

    return (
        <div className={ "block-viewer" }>
            <ConfirmPopup />
            <div className="block-section">
                <div className="block-header">
                    <span className="block-title">
                        <h5 className={ "mb-0" }>
                            { t("providers") } ({ total })
                        </h5>
                    </span>
                </div>
                <div className="block-content">
                    <Row className={ "p-2" }>
                        <Col sm={ 12 } md={ 6 } className={ "mb-2" }>
                            <Formik enableReinitialize={ true } initialValues={ initSearchFormState } onSubmit={ onSearch }>
                                {(fp: FormikProps<any>) => (
                                    <Form onSubmit={ fp.handleSubmit }>
                                        <Col sm={ 12 } md={ 8 }>
                                            <Field
                                                component={ InputField }
                                                withoutLabel={ true }
                                                autoFocus={ true }
                                                name="search"
                                                formGroupClass={ "mb-0" }
                                                placeholder={ t("search") }
                                            />
                                        </Col>
                                    </Form>
                                )}
                            </Formik>
                        </Col>
                        <Col sm={ 12 } md={ 6 } className={ "text-right mb-2" }>
                            <div className="d-inline-block me-2 align-middle text-left">
                                <QuickCopyDropdown
                                    columns={ columns }
                                    tableName={ "supplierSettingsProviderList" }
                                    selectedRows={ selectedRows }
                                    trackBy={ "id" }
                                    // @ts-ignore
                                    entities={ tableRef.current?.getEntities?.() || [] }
                                />
                            </div>
                            <ButtonGroup>
                                <HasAccess hasAllAccess={ ["providersstore"] }>
                                    <Button
                                        size={ "small" }
                                        outlined
                                        tooltip={ t("add item") }
                                        tooltipOptions={ { position: "top" } }
                                        onClick={ () => onOperationClick("add") }
                                    >
                                        <i className="pi pi-plus" />
                                        <span className={ "ms-1" }>{ t("add") }</span>
                                    </Button>
                                </HasAccess>
                                <Button
                                    icon="pi pi-filter-fill"
                                    size={ "small" }
                                    outlined
                                    tooltip={ t("filter") }
                                    tooltipOptions={ { position: "top" } }
                                    onClick={ () => setFilterPanel(true) }
                                />
                                <Button
                                    icon="pi pi-file-excel"
                                    size={ "small" }
                                    outlined
                                    tooltip={ t("excel export") }
                                    tooltipOptions={ { position: "top" } }
                                    onClick={ () => setExcelPanel(true) }
                                />
                                <Button
                                    icon="pi pi-refresh"
                                    size={ "small" }
                                    outlined
                                    tooltip={ t("reset") }
                                    tooltipOptions={ { position: "top" } }
                                    onClick={ () => onReset(true) }
                                />
                            </ButtonGroup>
                        </Col>
                    </Row>

                    <StateLessGrid
                        size={ "normal" }
                        url={ "/suppliers/settings/providers?1=1" }
                        name={ "supplierSettingsProviderList" }
                        columns={ columns }
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        ref={ tableRef }
                        itemSize={ 45 }
                        setTotal={ setTotal }
                        onRowSelection={ setSelectedRows }
                        stickyHeader={ true }
                        showQuickCopy={ false }
                        state={ true }
                    />

                    <ProviderForm params={ { operation, operationId } } toastify={ toastify } onClose={ onCloseOperation } />
                    <ProviderFilter
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        visibility={ filterPanel }
                        onClose={ () => setFilterPanel(false) }
                    />
                    <ProviderExcel
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        visibility={ excelPanel }
                        onClose={ () => {
                            setExcelPanel(false);
                            focusOnSearch();
                        } }
                    />
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(ProviderList);
